/**
 * Un campo del detalle con edición propia (lápiz).
 *
 * Por defecto muestra solo el valor. Si `puedeEditar`, aparece un lápiz (solo al pasar el mouse o con el
 * foco dentro; siempre tenue en pantallas táctiles) y un clic sobre el valor también abre el campo, salvo
 * que el clic sea sobre un enlace o botón del valor. Abre únicamente este campo
 * con su control y Guardar/Cancelar; guarda con un PATCH de ese único campo. El borrador es estado
 * local y se copia desde el servidor solo al abrir: la recarga en vivo (30 s) no lo pisa.
 * Escape cancela la edición sin cerrar el panel; al terminar el foco vuelve al lápiz.
 * Mientras guarda, el botón muestra un giro y «Guardando…» (deshabilitado: sin doble envío); al terminar,
 * «Guardado» con un check durante `GUARDADO_VISIBLE_MS`, encima de la esquina del lápiz (no mueve nada).
 * Un control con acciones propias (el bloque «Nuevo proyecto») puede ocultar Guardar/Cancelar y guardar
 * de una vez con `guardarCon`, para que nunca haya dos juegos de botones a la vista.
 */
import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react';
import type { ActualizarTareaEntrada } from '@cdtec-backlog/contrato';
import { Giro } from '../../componentes/Esqueleto';
import { Icono } from '../../componentes/Icono';
import { mensajeDeError } from '../../lib/api';
import { useActualizarTarea } from './hooks';
import './CampoEditable.css';

/** Textarea que crece con su contenido: el único scroll es el del panel. */
export function AreaAutoAlto({
  value,
  onChange,
  ...resto
}: {
  id: string;
  value: string;
  maxLength: number;
  onChange: (evento: ChangeEvent<HTMLTextAreaElement>) => void;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    if (el.scrollHeight > 0) el.style.height = `${el.scrollHeight}px`;
  }, [value]);
  return <textarea ref={ref} value={value} onChange={onChange} {...resto} />;
}

/** Cuánto se ve «Guardado» después de guardar un campo. */
export const GUARDADO_VISIBLE_MS = 2000;

export interface ControlCampo<T> {
  id: string;
  borrador: T;
  cambiar: (valor: T) => void;
  /** Para `aria-invalid` y `aria-describedby` del control. */
  propsError: { 'aria-invalid'?: boolean; 'aria-describedby'?: string };
  /** Guarda de una vez con este valor (p. ej. el proyecto recién creado), con la misma validación y errores. */
  guardarCon: (valor: T) => void;
  /** El control abrió sus propias acciones (bloque «Nuevo proyecto»): oculta Guardar/Cancelar del campo. */
  ocultarAcciones: (ocultas: boolean) => void;
}

interface Props<T extends string | boolean> {
  tareaId: number;
  /** Nombre del campo, p. ej. «Título». Se muestra como título salvo que `ocultarEtiqueta`. */
  etiqueta: string;
  /** El título ya está en otro lado (la `dt` de la propiedad, el título grande); queda solo para lectores. */
  ocultarEtiqueta?: boolean;
  /** `seccion` = texto largo con título de sección; `propiedad` = valor corto; `titulo` = el título de la tarea. */
  variante?: 'seccion' | 'propiedad' | 'titulo';
  /** Texto del lápiz para lectores; por defecto «Editar <etiqueta en minúscula>». */
  nombreLapiz?: string;
  puedeEditar: boolean;
  /** Valor actual en el servidor; se copia al borrador al abrir. */
  valor: T;
  /** Cómo se ve en lectura. */
  vista: ReactNode;
  /** Convierte el borrador en el cambio parcial que se envía. */
  cambios: (borrador: T) => ActualizarTareaEntrada;
  /** Devuelve un mensaje si el borrador no es válido. */
  validar?: (borrador: T) => string | null;
  control: (c: ControlCampo<T>) => ReactNode;
  /** El control ya trae su propia etiqueta (p. ej. el selector de proyecto). */
  controlConEtiqueta?: boolean;
  /** Cómo mostrar un error de la API junto al campo; por defecto el mensaje general. */
  mensajeError?: (falla: unknown) => string;
}

export function CampoEditable<T extends string | boolean>({
  tareaId,
  etiqueta,
  ocultarEtiqueta = false,
  variante = 'propiedad',
  nombreLapiz,
  puedeEditar,
  valor,
  vista,
  cambios,
  validar,
  control,
  controlConEtiqueta = false,
  mensajeError = mensajeDeError,
}: Props<T>) {
  const id = useId();
  const idError = `${id}-error`;
  const [editando, setEditando] = useState(false);
  const [borrador, setBorrador] = useState<T>(valor);
  const [error, setError] = useState<string | null>(null);
  const [accionesOcultas, setAccionesOcultas] = useState(false);
  const [recienGuardado, setRecienGuardado] = useState(false);
  const lapiz = useRef<HTMLButtonElement>(null);
  const cuerpo = useRef<HTMLFormElement>(null);
  // Estado y no ref: `cerrar` llega al control (vía `guardarCon`) durante el render, y ahí no se tocan refs.
  const [devolverFoco, setDevolverFoco] = useState(false);
  const actualizar = useActualizarTarea(tareaId, { errorEnLinea: true });

  useEffect(() => {
    if (!recienGuardado) return;
    const temporizador = window.setTimeout(() => setRecienGuardado(false), GUARDADO_VISIBLE_MS);
    return () => window.clearTimeout(temporizador);
  }, [recienGuardado]);

  useEffect(() => {
    if (editando) {
      cuerpo.current?.querySelector<HTMLElement>('input, select, textarea')?.focus();
    } else if (devolverFoco) {
      lapiz.current?.focus();
    }
  }, [editando, devolverFoco]);

  const abrir = () => {
    setRecienGuardado(false);
    setBorrador(valor);
    setError(null);
    setAccionesOcultas(false);
    setDevolverFoco(false);
    setEditando(true);
  };
  const cerrar = () => {
    setDevolverFoco(true);
    setEditando(false);
    setError(null);
    setAccionesOcultas(false);
  };

  const guardarCon = (nuevo: T) => {
    const invalido = validar?.(nuevo) ?? null;
    if (invalido) {
      setError(invalido);
      return;
    }
    if (nuevo === valor) {
      cerrar();
      return;
    }
    setError(null);
    actualizar.mutate(cambios(nuevo), {
      onSuccess: () => {
        cerrar();
        setRecienGuardado(true);
      },
      onError: (falla) => setError(mensajeError(falla)),
    });
  };

  const guardar = (evento: FormEvent) => {
    evento.preventDefault();
    guardarCon(borrador);
  };

  const alTeclear = (evento: KeyboardEvent) => {
    if (evento.key !== 'Escape') return;
    // Cancela solo esta edición: el panel ignora los Escape ya atendidos.
    evento.preventDefault();
    cerrar();
  };

  // Clic sobre el valor abre la edición, pero no si fue sobre un enlace o botón del valor ni si la persona
  // estaba seleccionando texto para copiarlo.
  const alHacerClic = (evento: MouseEvent<HTMLDivElement>) => {
    if (!puedeEditar) return;
    const objetivo = evento.target;
    if (objetivo instanceof Element && objetivo.closest('a, button, input, select, textarea'))
      return;
    if (window.getSelection()?.toString()) return;
    abrir();
  };

  const clases = [
    'cedit',
    `cedit--${variante}`,
    puedeEditar && !editando ? 'cedit--editable' : '',
    editando ? 'cedit--editando' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={clases}>
      {!ocultarEtiqueta && (
        <h3 className="cedit__etiqueta" aria-hidden={editando ? true : undefined}>
          {etiqueta}
        </h3>
      )}
      {editando ? (
        <form ref={cuerpo} className="cedit__form" onSubmit={guardar} onKeyDown={alTeclear}>
          {!controlConEtiqueta && (
            <label className="solo-lectores" htmlFor={id}>
              {etiqueta}
            </label>
          )}
          {control({
            id,
            borrador,
            cambiar: setBorrador,
            propsError: error ? { 'aria-invalid': true, 'aria-describedby': idError } : {},
            guardarCon,
            ocultarAcciones: setAccionesOcultas,
          })}
          {error && (
            <p id={idError} role="alert" className="cedit__error">
              {error}
            </p>
          )}
          {!accionesOcultas && (
            <div className="cedit__acciones">
              <button
                type="submit"
                className="boton boton--primario boton--con-giro"
                disabled={actualizar.isPending}
              >
                {actualizar.isPending && <Giro />}
                {actualizar.isPending ? 'Guardando…' : 'Guardar'}
              </button>
              <button
                type="button"
                className="boton"
                disabled={actualizar.isPending}
                onClick={cerrar}
              >
                Cancelar
              </button>
            </div>
          )}
        </form>
      ) : (
        <>
          {/* El clic con el mouse es un atajo: con teclado se usa el lápiz, que sí es un botón. */}
          <div className="cedit__valor" onClick={alHacerClic}>
            {vista}
          </div>
          {puedeEditar && (
            <button
              type="button"
              ref={lapiz}
              className="cedit__lapiz"
              aria-label={nombreLapiz ?? `Editar ${etiqueta.toLowerCase()}`}
              onClick={abrir}
            >
              <Icono nombre="lapiz" tamano={14} />
            </button>
          )}
        </>
      )}
      {/* Región viva siempre presente (si apareciera con el texto, no se anunciaría). */}
      <span className="cedit__estado" role="status">
        {recienGuardado && (
          <>
            <Icono nombre="check" tamano={14} />
            Guardado
          </>
        )}
      </span>
    </div>
  );
}
